import Info from "@assets/icons/info.svg";
import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetUnifiedTransactionDetailsResponse,
  UnifiedTransactionDetailsData,
} from "@services/models/Transactions/GetUnifiedTransactionDetailsModel";
import React, { useEffect, useState } from "react";
import ConstantFunctions from "../../../utils/constants_functions";
import UnifiedTransactionDetailsSkeleton from "./components/UnifiedTransactionDetailsSkeleton";

interface UnifiedTransactionDetailsProps {
  transactionId: number;
  merchantId: number | undefined;
}

const UnifiedTransactionDetails: React.FC<UnifiedTransactionDetailsProps> = ({
  transactionId,
  merchantId,
}) => {
  const constantFunctions = new ConstantFunctions();

  const [data, setData] = useState<UnifiedTransactionDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const constFunctions = new ConstantFunctions();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetUnifiedTransactionDetailsResponse =
        await transactionApiServices.getUnifiedTransactionDetails({
          transactionId: transactionId,
          merchantId: merchantId,
        });
      setData(response.data);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [transactionId]);

  if (error) {
    return (
      <div className="text-text-status-red text-[14px] font-normal">
        Fetch error
      </div>
    );
  }

  if (isLoading) {
    return <UnifiedTransactionDetailsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-[12px] mt-[16px]">
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-[8px] bg-bg-page-body py-[4px] px-[12px] rounded">
            <p className="text-text-base font-bold text-[10px]">
              Txn ID: {transactionId}
            </p>
            <CopyButton
              copyText={data?.transactionId.toString() ?? ""}
              copyIconSize={12}
              buttonSize="16px"
              copyMessage="Transaction ID Copied to clipboard"
            />
          </div>
          <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
            {data?.ledger == "CR" ? "Received" : "Sent"}
          </div>
          <DataPill value={data?.status ?? "pending"} />
        </div>
        <LabelRow
          label="Transaction Type"
          value={data?.transactionType ?? ""}
        />
        <LabelRow
          label="User Email"
          value={data?.userEmail ?? ""}
          isCopy={!!data?.userEmail}
        />
        {data?.firstName && data?.lastName && (
          <LabelRow
            label="Full Name"
            value={`${data.firstName} ${data.lastName}`}
          />
        )}
        {data?.merchantName && (
          <LabelRow label="Merchant Name" value={data.merchantName} />
        )}
        <LabelRow
          label="Trx Date & Time"
          value={constFunctions.formatDateTime(data?.createdAt!)}
        />
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label={data?.accountType === "PERSONAL" ? "UID" : "MID | UID"}
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              {data?.merchantId && (
                <>
                  <span className="text-text-base text-[12px] font-normal">
                    {data?.merchantId}
                  </span>

                  <CopyButton
                    copyText={data.merchantId.toString()}
                    copyMessage="Copied to clipboard"
                  />

                  <span className="text-text-base text-[12px] font-normal">
                    |
                  </span>
                </>
              )}
              <span className="text-text-base text-[12px] font-normal">
                {data?.userId}
              </span>
              <CopyButton
                copyText={data!.userId!.toString()}
                copyMessage="Copied to clipboard"
              />
            </div>
          }
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row items-center gap-[4px]">
          <div className="bg-bg-page-body rounded-full h-[24px] p-[2px] w-[24px] mr-[4px] flex items-center justify-center">
            <img src={Info} alt="Info" />
          </div>
          <span className="text-[14px] font-normal text-text-base">
            Payment Details
          </span>
        </div>
        <LabelRow
          label="Base Currency"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img
                src={data?.baseCurrencyImage}
                height={14}
                width={14}
                alt="currency icon"
              />
              <p className="text-text-base text-[12px] font-normal">
                {data?.baseCurrency}
              </p>
            </div>
          }
        />
        <LabelRow
          label="Settled Currency"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img
                src={data?.requestedCurrencyImage}
                height={14}
                width={14}
                alt="currency icon"
              />
              <p className="text-text-base text-[12px] font-normal">
                {data?.requestedCurrency}
              </p>
            </div>
          }
        />
        <LabelRow label="Network" value={data?.network} />
        <LabelRow
          label="OID"
          value={data?.orderId ?? ""}
          isCopy={!!data?.orderId}
        />

        {data?.merchantOrderId && (
          <LabelRow
            label="MOID"
            value={data?.merchantOrderId ?? ""}
            valueComp={
              <TruncatedCopyValue
                value={data?.merchantOrderId}
                copyMessage="MOID copied to clipboard"
              />
            }
          />
        )}

        <LabelRow
          label="Base Amount"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {data?.baseAmount?.toFixed(2)} {data?.baseCurrency}
              </span>
            </div>
          }
        />

        <LabelRow
          label="Requested Amount"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {data?.requestedAmount?.toFixed(2)} {data?.requestedCurrency}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Conversion Rate"
          value={data?.conversionRate.toString()}
        />
        {data?.commissionAmount !== undefined && (
          <LabelRow
            label="Commission Amount"
            value={`${data.commissionAmount?.toFixed(2)} ${
              data?.requestedCurrency
            }`}
          />
        )}
        {data?.networkFees !== undefined && (
          <LabelRow
            label="Network Fees"
            value={`${data.networkFees?.toFixed(2)} ${data?.requestedCurrency}`}
          />
        )}
        {data?.totalFees !== undefined && (
          <LabelRow
            label="Total Fees"
            value={`${data.totalFees?.toFixed(2)} ${data?.requestedCurrency}`}
          />
        )}

        <LabelRow
          label={data?.ledger === "CR" ? "Received Amount" : "Sent Amount"}
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {data?.receivedAmount?.toFixed(2)} {data?.requestedCurrency}
              </span>
            </div>
          }
        />

        {data?.baseAmountReceived !== undefined && (
          <LabelRow
            label={data?.ledger === "CR" ? "Base Amount Received" : "Base Amount Sent"}
            value={`${data.baseAmountReceived?.toFixed(2)} ${
              data?.baseCurrency
            }`}
          />
        )}
      </div>

      {data?.transactionLogs && data.transactionLogs.length > 0 && (
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <div className="rounded-lg flex flex-col my-2">
            <div className="flex flex-col rounded-lg p-[8px] bg-bg-trx-card-header rounded-t-lg rounded-b-none">
              <span className="text-[12px] font-semibold text-center">
                Transaction Details
              </span>
            </div>
            <div className="flex flex-col bg-[#FFFFFF] p-[8px] rounded-t-none rounded-b-lg">
              {data.transactionLogs.map((log, index) => {
                return (
                  <div key={index}>
                    <div className="flex flex-row justify-between mb-1">
                      <span className="font-medium text-[12px] text-text-label">
                        From address
                      </span>
                      <CopyButton
                        copyText={log.fromAddress ?? ""}
                        copyMessage="Address copied to clipboard!"
                        fillColor=""
                      />
                    </div>
                    <span className="text-text-base font-medium text-[12px] break-all mb-2">
                      {log.fromAddress ?? "- -"}
                    </span>
                    <div className="flex flex-row justify-between">
                      <span className="font-medium text-[12px] text-text-label">
                        To address
                      </span>
                      <CopyButton
                        copyText={log.toAddress ?? ""}
                        copyMessage="Address copied to clipboard!"
                        fillColor=""
                      />
                    </div>
                    <span className="text-text-base font-medium text-[12px] break-all mt-1">
                      {log.toAddress ?? "- -"}
                    </span>
                    <div className="flex flex-row justify-between w-full mt-[12px]">
                      <span className="font-medium text-[12px] text-text-label">
                        Transaction Hash
                      </span>
                      <div className="flex flex-row gap-[8px]">
                        <CopyButton
                          copyText={log.transactionHash ?? ""}
                          copyMessage="Transaction Hash copied to clipboard!"
                          fillColor=""
                        />
                      </div>
                    </div>
                    <span className="text-text-base font-medium text-[12px] break-all mt-1">
                      {log.transactionHash}
                    </span>
                    <div className=" p-[8px] mt-[12px] flex flex-row justify-between w-full border-t border-t-border-subtle">
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-[12px] text-text-label">
                          Amount
                        </span>
                        <span className="font-medium text-[12px] text-text-base">
                          {constantFunctions.formatAmount(log.amount)}{" "}
                          {data?.requestedCurrency}
                        </span>
                      </div>
                      <div className="w-px bg-border-subtle" />
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-[12px] text-text-label">
                          Confirmations
                        </span>
                        <span className="font-medium text-[12px] text-text-base">
                          {log.confirmations! >= log.requiredConfirmations ? (
                            <>
                              {log.requiredConfirmations} {" / "}
                              {log.requiredConfirmations}
                            </>
                          ) : (
                            <>
                              {log.confirmations} {" / "}
                              {log.requiredConfirmations}
                            </>
                          )}
                        </span>
                      </div>
                      <div className="w-px bg-border-subtle" />
                      <div className="flex flex-col">
                        <span className="font-medium text-[12px] text-text-label text-right">
                          Status
                        </span>
                        <DataPill
                          value={
                            log.confirmations! >= log.requiredConfirmations
                              ? "confirmed"
                              : "pending"
                          }
                        />
                      </div>
                    </div>
                    {index < data.transactionLogs!.length - 1 && (
                      <div className="border-b border-border-subtle my-[12px]" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedTransactionDetails;
